import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";

export default function PaymentResult() {
  const loc = useLocation();
  const [params] = useSearchParams();
  const cancelled = loc.pathname.includes("cancel");
  const provider = params.get("provider");
  const isPublishing = params.get("kind") === "publishing";
  const dashLink = isPublishing ? "/my-publishing" : "/advertiser";
  const dashLabel = isPublishing ? "View my publishing" : "View my campaigns";
  const sessionId = provider === "bitpay" ? localStorage.getItem("pti_bitpay_invoice") : params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "polling");
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (cancelled || !sessionId) return;
    if (tries > 8) { setState(provider === "bitpay" ? "pending_crypto" : "timeout"); return; }
    const t = setTimeout(() => {
      const url = provider === "bitpay" ? `/payments/bitpay/status/${sessionId}` : `/payments/status/${sessionId}`;
      api.get(url).then((r) => {
        if (r.data.payment_status === "paid") setState("paid");
        else if (["expired", "failed", "invalid", "declined"].includes(r.data.payment_status || r.data.status)) setState("failed");
        else setTries((n) => n + 1);
      }).catch(() => setTries((n) => n + 1));
    }, 1800);
    return () => clearTimeout(t);
  }, [tries, sessionId, cancelled, provider]);

  return (
    <div className="pti-container py-24 max-w-lg text-center" data-testid="payment-result">
      {state === "polling" && <><Loader2 className="mx-auto animate-spin text-pti-red" size={48} /><h1 className="font-display text-3xl font-black mt-4">Confirming your payment…</h1><p className="text-gray-600 mt-2">Hang tight, this only takes a moment.</p></>}
      {state === "pending_crypto" && <>
        <Loader2 className="mx-auto text-pti-red" size={48} />
        <h1 className="font-display text-3xl font-black mt-4">Payment Pending</h1>
        <p className="text-gray-600 mt-2">Your crypto payment is being confirmed on the network via BitPay. Your campaign will move to "Paid — Pending Approval" automatically once confirmed — you can safely close this page and check your dashboard.</p>
        <Link to="/advertiser" className="inline-block mt-6 bg-pti-red text-white px-6 py-3 font-bold hover:bg-pti-redhover transition-colors">View my campaigns</Link>
      </>}
      {state === "paid" && <>
        <CheckCircle2 className="mx-auto text-green-600" size={56} />
        <h1 className="font-display text-3xl font-black mt-4">{isPublishing ? "Paid — Submitted for Review" : "Paid — Pending Approval"}</h1>
        <p className="text-gray-600 mt-2">{isPublishing
          ? "Thank you! Your article is paid and now with our editorial desk for review. You'll see its status update in My Publishing — approval isn't guaranteed, and declined submissions are refunded."
          : "Thank you! Your campaign is paid and now awaiting admin approval. It will go live on your scheduled dates once approved."}</p>
        <Link to={dashLink} className="inline-block mt-6 bg-pti-red text-white px-6 py-3 font-bold hover:bg-pti-redhover transition-colors">{dashLabel}</Link>
      </>}
      {(state === "cancelled" || state === "failed" || state === "timeout") && <>
        <XCircle className="mx-auto text-pti-red" size={56} />
        <h1 className="font-display text-3xl font-black mt-4">{state === "cancelled" ? "Payment cancelled" : "Payment not completed"}</h1>
        <p className="text-gray-600 mt-2">{isPublishing
          ? "No charge was made. You can complete your publishing payment anytime from My Publishing."
          : "No charge was made. Your campaign is saved as a draft — you can pay for it anytime."}</p>
        <Link to={dashLink} className="inline-block mt-6 border border-pti-black px-6 py-3 font-bold hover:bg-pti-black hover:text-white transition-colors">Back to {isPublishing ? "my publishing" : "dashboard"}</Link>
      </>}
    </div>
  );
}
